import React, { useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../services/axiosInterceptor';
import OfficialDocLayout from '../../adminOffice/print/OfficialDocLayout';
import { outcomeMeta, sourceLabel, timeOf } from '../nurseOptions';

// ใบส่งตัวนักเรียนไปสถานพยาบาล
//
// พิมพ์จากแถว tb_nurse_visit ตรงๆ ไม่ให้กรอกใหม่ ไม่งั้นจะมีสองความจริง:
// ใบกระดาษที่ รพ. ถืออยู่กับบันทึกในระบบไม่ตรงกัน แล้วไม่มีทางรู้ว่าอันไหนถูก
//
// โรคประจำตัว/การแพ้ยา อยู่บนใบด้วยโดยตั้งใจ — เป็นข้อมูลที่ รพ. ต้องรู้ก่อนให้ยา
// และเป็นเหตุผลหลักที่ใบนี้ควรมาจากระบบแทนการเขียนมือ

const fmtThaiFull = (ymd) => {
  if (!ymd) return '.................................';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  const months = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
  return `${d} ${months[m]} ${y + 543}`;
};

const classText = (v) => {
  if (!v?.c_class) return '-';
  const level = String(v.c_class).replace(/[^0-9]/g, '');
  return level ? `ม.${level}/${v.r_no}` : `${v.c_class}/${v.r_no}`;
};

const Line = ({ label, value, width = '100%' }) => (
  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
    <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
    <span style={{
      flexGrow: 1, width, borderBottom: '1px dotted #000',
      paddingLeft: 6, minHeight: '1.2em',
    }}>{value || ''}</span>
  </div>
);

export default function ReferralDoc() {
  const { nvId } = useParams();
  const [visit, setVisit] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axiosInstance.get(`/api/nurse/visits/${nvId}`)
      .then(res => setVisit(res.data))
      .catch(err => setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'));
  }, [nvId]);

  if (error) return <div className="p-4"><Alert variant="danger">{error}</Alert></div>;
  if (!visit) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

  const vitals = [
    visit.nv_temp ? `อุณหภูมิ ${visit.nv_temp} °C` : null,
    visit.nv_pulse ? `ชีพจร ${visit.nv_pulse} ครั้ง/นาที` : null,
    visit.nv_bp_sys ? `ความดัน ${visit.nv_bp_sys}/${visit.nv_bp_dia} mmHg` : null,
    visit.nv_spo2 ? `SpO₂ ${visit.nv_spo2}%` : null,
  ].filter(Boolean).join('  ·  ');

  const symptom = [visit.ns_name, visit.nv_symptom_detail].filter(Boolean).join(' — ');

  return (
    <OfficialDocLayout title="ใบส่งตัวนักเรียนเข้ารับการรักษา" backLink="/nurse/visits">
      <div className="doc-no" style={{ marginTop: -18 }}>
        วันที่ {fmtThaiFull(visit.nv_date)}
      </div>

      <div style={{ marginTop: 10 }}>
        <Line label="ข้าพเจ้า" value={visit.recorded_by_name} width="55%" />
        <Line label="ตำแหน่ง เจ้าหน้าที่ห้องพยาบาล ขอส่งตัวนักเรียนชื่อ" value={visit.st_name} />
        <Line label="เลขประจำตัว" value={visit.st_idnumber} width="30%" />
        <div style={{ display: 'flex', gap: 16 }}>
          <Line label="ชั้น" value={classText(visit)} width="30%" />
          <Line label="ปีการศึกษา" value={visit.year_year} width="30%" />
        </div>
        <Line label="เข้ารับการรักษาที่" value={visit.nv_refer_place} />
      </div>

      <div style={{ marginTop: 14, border: '1px solid #000', padding: '8px 12px' }}>
        <div style={{ fontWeight: 'bold' }}>ข้อมูลจากห้องพยาบาล</div>
        <Line label="มาห้องพยาบาลเมื่อเวลา"
          value={`${timeOf(visit.nv_check_in)} น. (${sourceLabel(visit.nv_source)})`} width="55%" />
        <Line label="อาการ" value={symptom} />
        <Line label="สัญญาณชีพ" value={vitals} />
        <Line label="การรักษาเบื้องต้น" value={visit.nv_treatment} />
        <Line label="ผลการพิจารณา" value={outcomeMeta(visit.nv_outcome).label} width="50%" />
      </div>

      <div style={{ marginTop: 12, border: '1px solid #000', padding: '8px 12px' }}>
        <div style={{ fontWeight: 'bold' }}>ข้อมูลสุขภาพที่ต้องทราบก่อนให้การรักษา</div>
        <Line label="โรคประจำตัว" value={visit.st_syndrome} />
        <Line label="ประวัติการแพ้ยา" value={visit.st_drug} />
        <Line label="ประวัติการแพ้อาหาร" value={visit.st_food} />
        <Line label="วิธีแก้ไขเมื่อเจ็บป่วยด้วยโรคประจำตัว" value={visit.st_facingSyndrome} />
        <Line label="เบอร์ติดต่อผู้ปกครอง" value={visit.st_parentPhone} width="40%" />
      </div>

      <p style={{ marginTop: 14, textIndent: '2.5cm' }}>
        จึงเรียนมาเพื่อโปรดพิจารณาให้การรักษาแก่นักเรียนดังกล่าว และขอความอนุเคราะห์แจ้งผลการรักษา
        กลับมายังโรงเรียนเพื่อใช้ในการดูแลนักเรียนต่อไป
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 40 }}>
        <div style={{ textAlign: 'center', width: '45%' }}>
          <div>ลงชื่อ ................................................</div>
          <div style={{ marginTop: 6 }}>({visit.recorded_by_name || '.............................'})</div>
          <div>ผู้ส่งตัว</div>
        </div>
        <div style={{ textAlign: 'center', width: '45%' }}>
          <div>ลงชื่อ ................................................</div>
          <div style={{ marginTop: 6 }}>(.............................................)</div>
          <div>ผู้รับตัวนักเรียน</div>
        </div>
      </div>
    </OfficialDocLayout>
  );
}
